import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useTasks, Task } from '@/hooks/useTasks';
import { useMemo, useState } from 'react';
import { format, differenceInDays, addDays, formatDistanceToNow, parseISO, startOfDay } from 'date-fns';
import { CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { TaskFormDialog } from '@/components/tasks/TaskFormDialog';

const getPriorityColor = (priority: string): string => {
  switch (priority) {
    case 'high':
      return 'bg-destructive';
    case 'medium':
      return 'bg-warning';
    case 'low':
      return 'bg-muted-foreground';
    default:
      return 'bg-muted-foreground';
  }
};

const formatDueDate = (dueDate: string, dueTime?: string | null): string => {
  const today = startOfDay(new Date());
  const due = startOfDay(parseISO(dueDate));
  
  const diffDays = differenceInDays(due, today);
  
  if (diffDays < 0) {
    return `Overdue ${formatDistanceToNow(due, { addSuffix: true })}`;
  }
  if (diffDays === 0) {
    if (dueTime) {
      const timeDate = new Date(`${dueDate}T${dueTime}`);
      return `Today at ${format(timeDate, 'h:mm a')}`;
    }
    return 'Due today';
  }
  if (diffDays === 1) {
    if (dueTime) {
      const timeDate = new Date(`${dueDate}T${dueTime}`);
      return `Tomorrow at ${format(timeDate, 'h:mm a')}`;
    }
    return 'Due tomorrow';
  }
  if (diffDays <= 7) {
    return `Due ${format(due, 'EEEE')}`;
  }
  return `Due ${format(due, 'MMM d')}`;
};

export function UpcomingTasks() {
  const { tasks, isLoading } = useTasks();
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  const upcomingTasks = useMemo(() => {
    if (!tasks) return [];
    
    const today = startOfDay(new Date());
    const sevenDaysFromNow = addDays(today, 7);
    
    return tasks
      .filter(task => {
        // Exclude completed tasks
        if (task.status === 'done') return false;
        
        // Include tasks with no due date
        if (!task.due_date) return false;
        
        const dueDate = startOfDay(parseISO(task.due_date));
        
        // Include overdue tasks OR tasks due within 7 days
        return dueDate <= sevenDaysFromNow;
      })
      .sort((a, b) => {
        const today = startOfDay(new Date());
        
        // Sort by overdue first
        const aDate = startOfDay(parseISO(a.due_date!));
        const bDate = startOfDay(parseISO(b.due_date!));
        
        const aOverdue = aDate < today;
        const bOverdue = bDate < today;
        
        if (aOverdue && !bOverdue) return -1;
        if (!aOverdue && bOverdue) return 1;
        
        // Then by priority
        const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
        const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
        if (priorityDiff !== 0) return priorityDiff;
        
        // Then by due date
        return aDate.getTime() - bDate.getTime();
      })
      .slice(0, 6);
  }, [tasks]);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Upcoming Tasks</CardTitle>
          <CardDescription>Things you need to focus on</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center space-x-4">
                <Skeleton className="w-2 h-2 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (upcomingTasks.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Upcoming Tasks</CardTitle>
          <CardDescription>Things you need to focus on</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CheckCircle className="h-12 w-12 text-success mb-3" />
            <p className="text-sm font-medium text-foreground">All caught up!</p>
            <p className="text-sm text-muted-foreground mt-1">
              No upcoming tasks at the moment
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upcoming Tasks</CardTitle>
        <CardDescription>Things you need to focus on</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2">
          {upcomingTasks.map((task) => {
            const today = startOfDay(new Date());
            const dueDate = startOfDay(parseISO(task.due_date!));
            const isOverdue = dueDate < today;

            return (
              <div 
                key={task.id} 
                className="flex items-center space-x-4 p-2 -mx-2 rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => setEditingTask(task)}
              >
                <div className={cn(
                  'w-2 h-2 rounded-full flex-shrink-0',
                  isOverdue ? 'bg-destructive' : getPriorityColor(task.priority)
                )} />
                <div className="flex-1 space-y-1 min-w-0">
                  <p className="text-sm font-medium leading-none">
                    {task.title}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={cn(
                      "text-sm",
                      isOverdue ? "text-destructive font-medium" : "text-muted-foreground"
                    )}>
                      {formatDueDate(task.due_date!, task.due_time)}
                    </p>
                    {task.status === 'in_progress' && (
                      <Badge variant="secondary" className="text-xs">
                        In Progress
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="mt-4 pt-4 border-t">
          <Link 
            to="/tasks"
            className="text-sm text-primary hover:text-primary/80 font-medium flex items-center gap-1 transition-colors"
          >
            View all tasks
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </CardContent>
      <TaskFormDialog
        open={!!editingTask}
        onOpenChange={(open) => !open && setEditingTask(null)}
        task={editingTask || undefined}
      />
    </Card>
  );
}
